import { zodResolver } from '@hookform/resolvers/zod'
import { Paperclip } from 'lucide-react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'

import { useUpdatePost } from 'src/features/feeds/api/feeds/use-update-post'
import { useUploadImages } from 'src/features/feeds/api/feeds/use-upload-images'
import { logger } from 'src/lib/logger'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { TextareaFormField } from 'src/shared/components/ui/forms/form-fields/textarea'
import type { StreamPost } from 'src/shared/schemas/stream/feed'
import { PostSchema } from 'src/shared/schemas/stream/feed'
import { cn } from 'src/shared/utils/cn'

interface PostEditorProps {
  post: StreamPost
  onCancel: () => void
  onSuccess: () => void
}

const PostEditor = ({ post, onCancel, onSuccess }: PostEditorProps) => {
  const [editImages, setEditImages] = useState<string[]>(post.images || [])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const updatePost = useUpdatePost()
  const uploadImages = useUploadImages()

  const form = useForm({
    defaultValues: {
      text: post.text || post.object,
    },
    resolver: zodResolver(PostSchema),
  })

  const handleEdit = async () => {
    const { text } = form.getValues()
    if (!text.trim() || updatePost.isPending) return
    try {
      await updatePost.mutateAsync({
        id: post.id,
        text: text.trim(),
        images: editImages,
      })
      onSuccess()
    } catch (error) {
      logger.error('Post - Error updating post:', error)
    }
  }

  const handleRemoveImage = (index: number) => {
    setEditImages(editImages.filter((_, i) => i !== index))
  }

  const handleAddImages = async (files: File[]) => {
    if (isSubmitting) return
    try {
      setIsSubmitting(true)

      await updatePost.mutateAsync({
        id: post.id,
        text: form.getValues().text.trim(),
        images: [],
      })

      const urls = await uploadImages.mutateAsync({ files, postId: post.id })

      await updatePost.mutateAsync({
        id: post.id,
        text: form.getValues().text.trim(),
        images: [...editImages, ...urls],
      })

      setEditImages((prev) => [...prev, ...urls])
    } catch (error) {
      logger.error('Post - Error uploading images:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isLoading = updatePost.isPending || uploadImages.isPending

  return (
    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
      <Form {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleEdit()
          }}
          className="space-y-4"
        >
          <TextareaFormField control={form.control} name="text" minRows={1} maxRows={4} />
          <div className="flex items-center justify-between">
            <label
              className={cn(
                'flex cursor-pointer items-center gap-2',
                (editImages.length >= 5 || isLoading) && 'cursor-not-allowed opacity-50',
              )}
            >
              <Paperclip className="size-5" />
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || [])
                  if (files.length + editImages.length > 5) {
                    alert('Maximum 5 images allowed')
                    return
                  }
                  handleAddImages(files)
                }}
                disabled={editImages.length >= 5 || isLoading}
              />
            </label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={onCancel} isLoading={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={!form.getValues().text.trim()} isLoading={isLoading}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </Form>
      {editImages.length > 0 && (
        <div
          className={cn(
            'mb-4 grid gap-2',
            editImages.length === 1 && 'grid-cols-1',
            editImages.length === 2 && 'grid-cols-2',
            editImages.length >= 3 && 'grid-cols-3',
          )}
        >
          {editImages.map((image, index) => (
            <div key={index} className="relative">
              <img
                src={image}
                alt={`Post image ${index + 1}`}
                className="h-48 w-full cursor-pointer rounded object-cover"
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute right-2 top-2"
                onClick={() => handleRemoveImage(index)}
                isLoading={isLoading}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export type { PostEditorProps }
export default PostEditor
